import React, { useState, useEffect } from 'react';
import { useIdleTimeoutContext } from 'containers/InactivityTimeout/IdleTimeoutContext';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _omitBy from 'lodash/omitBy';
import _isEmpty from 'lodash/isEmpty';
import { emailRegex, getLowerCaseEmailDomain } from 'utils/stringUtils';
import useIsEmailVerified from 'components/Hooks/useIsEmailVerified';
import { useFirebase } from 'react-redux-firebase';
import { toast } from 'react-toastify';
import { DialogContent, Button } from '@material-ui/core';
import LoadingOverlay from 'components/LoadingOverlay';
import ThemedDialog from 'components/ThemedDialog';
import { useDispatch, useSelector } from 'react-redux';
import {
  makeSelectVerificationModal,
  makeSelectProfile,
} from 'containers/Auth/selectors';
import {
  showVerificationModal,
  hideVerificationModal,
  setVerificationInfo,
  toggleVerificationLoading,
} from 'containers/Auth/actions';
import { post } from 'utils/api';
import Config from 'utils/getEnvConfig';
import useContentfulLocale from 'components/useContentfulLocale';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { getBrandValue } from 'utils/brandUtils';
import Mixpanel from 'utils/mixpanelService';
import CloseIcon from './images/close.svg';

const {
  LAMBDA: {
    FIREBASE_HELPER: { REQUEST_EMAIL_VERIFICATION_URL, API_KEY },
  },
} = Config;

const EMAIL_VERIFICATION_API_OPTIONS = {
  headers: {
    'x-api-key': API_KEY,
  },
};

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: '20px !important',
    [theme.breakpoints.up('800')]: {
      width: 650,
      maxWidth: 650,
    },
    [theme.breakpoints.down('799')]: {
      margin: '12px',
    },
  },
  closeButton: {
    paddingTop: 24,
    paddingRight: 24,
    position: 'relative !important',
  },
  dialogContent: {
    padding: 0,
  },
  title: {
    ...theme.typography.h4,
    textAlign: 'center',
    fontFamily: theme.typography.h4.fontFamily,
    fontWeight: 600,
    marginBottom: 32,
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.h5.fontSize,
    },
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 5,
    paddingBottom: 74,
    [theme.breakpoints.down('sm')]: {},
  },
  subTitle: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  inputContainer: {
    width: '100%',
    margin: '0 auto',
    textAlign: 'center',
    marginBottom: 20,
    maxWidth: '80%',
    [theme.breakpoints.up('md')]: {
      maxWidth: 500,
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
    },
    '& .MuiOutlinedInput-input': {
      textAlign: 'left',
      padding: '0 13px',
      height: 48,
      fontSize: 16,
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: 10,
  },
  button: {
    minWidth: 100,
    height: 48,
    borderRadius: 8,
    textTransform: 'uppercase',
    fontSize: 14,
    fontWeight: 700,
    [theme.breakpoints.up('sm')]: {
      minWidth: 195,
    },
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: 16,
    marginTop: 12,
    textAlign: 'left',
  },
}));

const EditEmailModal = ({
  open,
  onClose,
  onSave,
  initialEmail,
  workEmailDomains,
  currentWorkEmail,
  currentPersonalEmail,
  isForceVerification,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { idleTimer } = useIdleTimeoutContext();
  const [email, setEmail] = useState(initialEmail || '');
  const [error, setError] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const { isEnabled } = useIsEmailVerified();
  const firebase = useFirebase();
  const verificationModalState = useSelector(makeSelectVerificationModal());
  const profile = useSelector(makeSelectProfile());
  const ctfLocale = useContentfulLocale();
  const [siteCopy, verifyEmailSiteCopy] = useSiteCopySelector(['account', 'verify-email']);

  // Derive flags from open prop
  const isUpdatingWorkEmail = open === 'work';
  const shouldUsePersonalEmail = open === 'personal';
  const isInitialPersonalEmailVerification =
    shouldUsePersonalEmail && !profile?.personalEmailVerified;

  // Dynamic event name prefix for Mixpanel tracking
  const eventPrefix = shouldUsePersonalEmail ? 'Personal ' : '';

  useEffect(() => {
    setEmail(initialEmail || '');
    setError('');
  }, [initialEmail, open]);

  // Pause idle timer when modal is open or verification modal is shown, resume when closed
  useEffect(() => {
    if (open || verificationModalState?.show) {
      idleTimer.pause();
    } else {
      idleTimer.start();
    }
  }, [open, verificationModalState?.show, idleTimer]);

  const validateEmail = newEmail => {
    if (!emailRegex.test(newEmail)) {
      return _get(
        siteCopy,
        'pageCopy.subscriptionInvalidEmailError',
        'Please enter a valid email address',
      );
    }

    if (shouldUsePersonalEmail) {
      if (!isForceVerification && newEmail === currentPersonalEmail) {
        return _get(
          siteCopy,
          'pageCopy.sameEmailError',
          'Please use a different email address',
        );
      }

      if (workEmailDomains.includes(getLowerCaseEmailDomain(newEmail))) {
        return _get(
          siteCopy,
          'pageCopy.subscriptionPersonalEmailError',
          'Please use a personal email address',
        );
      }
      if (newEmail === currentWorkEmail) {
        return _get(
          siteCopy,
          'pageCopy.subscriptionPersonalEmailError',
          'Please use a personal email address',
        );
      }
    } else {
      if (!isForceVerification && newEmail === currentWorkEmail) {
        return _get(
          siteCopy,
          'pageCopy.sameEmailError',
          'Please use a different email address',
        );
      }

      const isWorkEmailDomainError =
        !_isEmpty(workEmailDomains) &&
        !workEmailDomains.includes(getLowerCaseEmailDomain(newEmail));

      if (isWorkEmailDomainError) {
        const errorMessage = _get(
          siteCopy,
          'pageCopy.subscriptionWorkEmailDomainError',
          'Please use a work email address',
        );

        return errorMessage;
      }

      if (newEmail === currentPersonalEmail) {
        return _get(
          siteCopy,
          'pageCopy.subscriptionWorkEmailError',
          'Please use a different email address',
        );
      }
    }

    return '';
  };

  const handleEmailChange = e => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    const validationError = validateEmail(newEmail);
    setError(validationError);
  };

  const requestEmailVerification = async () => {
    try {
      dispatch(toggleVerificationLoading(true));

      // Force token refresh to get current email in token
      const { currentUser } = firebase.auth();
      const freshToken = await currentUser.getIdToken(true);

      const data = {
        brand: getBrandValue(),
        locale: ctfLocale.locale,
        isGuest: false,
        token: freshToken,
      };

      // For personal email verification without a verified email, use 'email' field
      // Otherwise, use 'newEmail' with EMAIL_CHANGE type
      if (isInitialPersonalEmailVerification) {
        data.email = email;
        data.account = 'personal';
      } else {
        data.newEmail = email;
        data.type = 'EMAIL_CHANGE';
        data.isEmailChange = true;
        if (shouldUsePersonalEmail) {
          data.account = 'personal';
        }
      }

      const response = await post(
        REQUEST_EMAIL_VERIFICATION_URL,
        _omitBy(data, _isNil),
        EMAIL_VERIFICATION_API_OPTIONS,
      );

      const responseData = _get(response, 'data');
      dispatch(
        setVerificationInfo({
          ...responseData,
          type: data.type,
          account: data.account, // Store account for resend functionality
        }),
      );

      return { success: true };
    } catch (requestError) {
      const errorType = _get(requestError, 'type');

      // Check if it's an email-related error that should be displayed in the modal
      const emailRelatedErrors = [
        'EMAIL_IN_USE',
        'PERSONAL_EMAIL_IN_USE',
        'NEW_EMAIL_IN_USE',
        'EMAIL_ALREADY_VERIFIED',
        'NEW_EMAIL_DIFFERENT_FROM_CURRENT_EMAIL',
      ];

      if (emailRelatedErrors.includes(errorType)) {
        // Get error message from site copy, with API error as fallback
        const errorLabels = _get(
          verifyEmailSiteCopy,
          ['pageCopy', 'verificationCodeLabels', 'errors'],
          {},
        );
        let errorMessage = _get(errorLabels, errorType);

        // If site copy doesn't have the label, try NEW_EMAIL_IN_USE -> EMAIL_IN_USE fallback
        if (
          !errorMessage &&
          ['PERSONAL_EMAIL_IN_USE', 'NEW_EMAIL_IN_USE'].includes(errorType)
        ) {
          errorMessage = _get(errorLabels, 'EMAIL_IN_USE');
        }

        // Use API error message as fallback
        if (!errorMessage) {
          errorMessage = _get(requestError, 'error');
        }

        // Final fallback to generic messages
        if (!errorMessage) {
          switch (errorType) {
            case 'EMAIL_IN_USE':
            case 'NEW_EMAIL_IN_USE':
              errorMessage = 'Email already in use by another account';
              break;
            case 'EMAIL_ALREADY_VERIFIED':
              errorMessage = 'Email already verified';
              break;
            case 'NEW_EMAIL_DIFFERENT_FROM_CURRENT_EMAIL':
              errorMessage = 'New email is different from current email';
              break;
            default:
              errorMessage = 'Unable to update email';
          }
        }

        // Return the error to be displayed in the modal
        return {
          success: false,
          isEmailError: true,
          error: errorMessage,
          errorType,
        };
      }

      // For other errors, still set verification info so dialog can handle them
      dispatch(
        setVerificationInfo({
          ...(requestError || {}),
          ...(isInitialPersonalEmailVerification
            ? { account: 'personal' }
            : {}),
        }),
      );
      return { success: true }; // Continue to show dialog for non-email errors
    } finally {
      dispatch(toggleVerificationLoading(false));
    }
  };

  const handleVerificationSuccess = async () => {
    setIsUpdatingEmail(true);
    try {
      // Call parent save handler (customToken and banner hiding handled in VerificationCodeDialog)
      await onSave(email, true);
      toast(_get(siteCopy, 'pageCopy.editEmailModalLabel.emailUpdateSuccess'), {
        type: 'success',
      });

      // Track successful email change
      Mixpanel.track(`${eventPrefix}Email Change - Successful`, {});

      dispatch(hideVerificationModal());
      handleClose();
    } catch (savingError) {
      if (savingError.code === 'auth/requires-recent-login') {
        setError(
          'This operation requires recent authentication. Please log in again and try updating your email.',
        );

        // Track failed email change
        Mixpanel.track(`${eventPrefix}Email Change - Failed`, {
          reason: 'requires recent authentication',
        });

        dispatch(hideVerificationModal());
        handleClose();
        return;
      }
      throw savingError;
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleSave = async () => {
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    // If email verification is enabled, request verification first
    if (isEnabled) {
      try {
        setIsUpdatingEmail(true);

        // Request email verification before showing the dialog
        const verificationResult = await requestEmailVerification();

        // If there's an email-related error, display it and don't show the dialog
        if (!verificationResult.success && verificationResult.isEmailError) {
          setError(verificationResult.error);

          // Track failed email change
          Mixpanel.track(`${eventPrefix}Email Change - Failed`, {
            reason: verificationResult.errorType || 'email verification failed',
          });

          return;
        }

        // No email error, show verification dialog
        dispatch(
          showVerificationModal({
            show: true,
            redirect: null,
            payload: {
              email,
              isGuest: false,
            },
            info: {
              type: isInitialPersonalEmailVerification
                ? undefined
                : 'EMAIL_CHANGE',
              isPersonalEmail: shouldUsePersonalEmail,
              callback: handleVerificationSuccess,
              showSuccessModal: false,
            },
          }),
        );
        // Keep the modal open so user can see the verification dialog
      } catch (saverError) {
        console.error('Error in handleSave:', saverError);
        setError(
          _get(siteCopy, 'pageCopy.editEmailModalLabel.emailUpdateError'),
        );

        // Track failed email change
        Mixpanel.track(`${eventPrefix}Email Change - Failed`, {
          reason: saverError.message || 'email update error',
        });
      } finally {
        setIsUpdatingEmail(false);
      }
    } else {
      // If verification is not enabled, update email directly
      try {
        setIsUpdatingEmail(true);
        const { currentUser } = firebase.auth();

        if (currentUser && isUpdatingWorkEmail && currentUser.email !== email) {
          await currentUser.updateEmail(email);
        }

        onSave(email);

        // Track successful email change
        Mixpanel.track(`${eventPrefix}Email Change - Successful`, {});
      } catch (updatingError) {
        if (updatingError.code === 'auth/requires-recent-login') {
          setError(
            'This operation requires recent authentication. Please log in again.',
          );

          // Track failed email change
          Mixpanel.track(`${eventPrefix}Email Change - Failed`, {
            reason: 'requires recent authentication',
          });
        } else {
          setError(
            _get(siteCopy, 'pageCopy.editEmailModalLabel.emailUpdateError'),
          );

          // Track failed email change
          Mixpanel.track(`${eventPrefix}Email Change - Failed`, {
            reason: updatingError.code || 'email update error',
          });
        }
      } finally {
        setIsUpdatingEmail(false);
      }
    }
  };

  const handleClose = () => {
    setEmail(initialEmail || '');
    setError('');
    setIsUpdatingEmail(false);
    dispatch(hideVerificationModal());
    onClose();
  };
  const buttons = [
    {
      label: _get(siteCopy, 'pageCopy.editEmailModalLabel.cancel'),
      color: 'default',
      variant: 'outlined',
      onClick: handleClose,
    },
    {
      label: _get(siteCopy, 'pageCopy.editEmailModalLabel.save'),
      color: 'primary',
      variant: 'contained',
      onClick: handleSave,
      disabled:
        !email ||
        !!error ||
        isUpdatingEmail ||
        (isForceVerification ? false : email === initialEmail),
    },
  ];

  const inputLabel = shouldUsePersonalEmail
    ? isForceVerification
      ? _get(
          siteCopy,
          'pageCopy.editEmailModalLabel.personalEmailVerificationPromptTitle',
        )
      : _get(siteCopy, 'pageCopy.editEmailModalLabel.inputLabelPersonal')
    : _get(siteCopy, 'pageCopy.editEmailModalLabel.inputLabel');

  const dialogProps = {
    onClose: handleClose,
    'aria-labelledby': 'remind-me-later-dialog',
    open: !!open,
    classes: {
      paper: classes.paper,
    },
    closeButtonClassName: classes.closeButton,
    CloseIconComponent: CloseIcon,
  };

  // Don't show the modal if verification modal is active
  if (verificationModalState?.show) {
    return null;
  }

  return (
    <ThemedDialog {...dialogProps} showCloseButton>
      <DialogContent className={classes.dialogContent}>
        <LoadingOverlay show={isUpdatingEmail} />
        <Box display="flex" justifyContent="center">
          <Box width="100%">
            <Box className={classes.modalContent}>
              <div className={classes.inputContainer}>
                <Typography className={classes.title}>{inputLabel}</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder={_get(
                    siteCopy,
                    'pageCopy.editEmailModalLabel.placeholder',
                  )}
                  className={classes.textField}
                  error={!!error}
                  type="email"
                  autoComplete="off"
                  disabled={isUpdatingEmail}
                />
                {error && (
                  <Typography className={classes.errorText}>{error}</Typography>
                )}
              </div>
              <div className={classes.buttonContainer}>
                {buttons.map(button => (
                  <Button
                    key={button.label}
                    onClick={button.onClick}
                    variant={button.variant}
                    color={button.color}
                    className={classes.button}
                    disabled={button.disabled}
                  >
                    {button.label}
                  </Button>
                ))}
              </div>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </ThemedDialog>
  );
};

EditEmailModal.propTypes = {
  open: PropTypes.PropTypes.oneOf(['work', 'personal']),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialEmail: PropTypes.string,
  workEmailDomains: PropTypes.array,
  currentWorkEmail: PropTypes.string,
  currentPersonalEmail: PropTypes.string,
  isForceVerification: PropTypes.bool,
};

EditEmailModal.defaultProps = {
  initialEmail: '',
  workEmailDomains: [],
  currentWorkEmail: '',
  currentPersonalEmail: '',
  isForceVerification: false,
};

export default EditEmailModal;
